import { ChevronLeft, Lock, LogIn, type LucideIcon } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/layout/Container";
import { FadeIn } from "@/components/motion/Stagger";
import { useAuth } from "@/hooks/useAuth";
import { useMySubscriptions } from "@/hooks/useSubscription";
import { cn } from "@/lib/cn";

/**
 * Shared scaffold for every study mode page. Renders a slim header with
 * back navigation, chapter context, and a slot for the actual viewer.
 */
export function ViewerScaffold({
  chapterTitle,
  subjectName,
  className,
  modeLabel,
  modeIcon: ModeIcon,
  modeAccent,
  showHeader = true,
  children,
}: {
  chapterTitle: string | null;
  subjectName: string | null;
  className?: string;
  modeLabel: string;
  modeIcon?: LucideIcon;
  modeAccent?: string;
  showHeader?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("flex flex-col min-h-[calc(100vh-3.5rem)]", className)}>
      {showHeader ? (
      <FadeIn>
        <Container size="full" className="pt-4 pb-3">
          <div className="flex items-baseline gap-2 flex-wrap text-xs text-text-muted">
            {subjectName ? (
              <>
                <Link to="/subjects" className="hover:text-text transition-colors">Subjects</Link>
                <span className="text-text-subtle">·</span>
                <span>{subjectName}</span>
              </>
            ) : (
              <Link to="/subjects" className="hover:text-text transition-colors">Subjects</Link>
            )}
            <span className="text-text-subtle">·</span>
            <span className="text-text font-medium truncate max-w-[40ch]">
              {chapterTitle || "Loading…"}
            </span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            {ModeIcon ? (
              <span
                className={cn(
                  "grid place-items-center h-7 w-7 rounded-md",
                  modeAccent ?? "bg-brand-soft text-brand",
                )}
              >
                <ModeIcon className="h-4 w-4" />
              </span>
            ) : null}
            <h1 className="font-display text-base sm:text-lg font-semibold tracking-tight">
              {modeLabel}
            </h1>
          </div>
        </Container>
      </FadeIn>
      ) : null}
      {children}
    </div>
  );
}

/**
 * Shown when the backend returns 403 / 404 for a signed-URL request,
 * or when our local entitlement check (useChapterAccess) says "no".
 *
 * Copy + CTA adapt to auth status:
 *   - anonymous       -> "Sign in to start" (links to login with ?next=)
 *   - authed, no sub  -> "Subscribe to <class>" (links to /subscribe)
 *   - authed, wrong class -> "not included" + billing / subscribed class CTAs
 *   - authed, no sub / generic locked -> "Subscribe" fallback
 *
 * The viewer pages still drive WHEN to render this card; this component
 * just owns the copy + CTA branching so we get a consistent surface.
 */
export function PaywallCard({
  modeLabel,
  classId,
  reason,
}: {
  modeLabel: string;
  classId?: string;
  /** Override the auto-detected reason. Useful when the viewer knows
   *  something more specific (e.g. "language not uploaded yet"). */
  reason?: "needs_auth" | "needs_subscription" | "wrong_class" | "locked";
}) {
  const { status } = useAuth();
  const mySubs = useMySubscriptions();
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);
  const subscribeHref = classId
    ? `/subscribe?class=${encodeURIComponent(classId)}`
    : "/subscribe";
  const activeSub = mySubs.data?.active ?? null;

  const inferredReason =
    status === "authenticated" && activeSub?.class && classId && activeSub.class !== classId
      ? "wrong_class"
      : status === "anonymous"
      ? "needs_auth"
      : "needs_subscription";

  const effectiveReason: "needs_auth" | "needs_subscription" | "wrong_class" | "locked" =
    reason ?? inferredReason;

  const isSignIn = effectiveReason === "needs_auth";
  const isWrongClass = effectiveReason === "wrong_class";
  const targetClassLabel = classId ? classLabel(classId) : "this class";
  const subscribedClassId = activeSub?.class;
  const subscribedClassLabel = subscribedClassId ? classLabel(subscribedClassId) : "your subscribed class";

  const title = isSignIn
    ? `Sign in to unlock ${modeLabel.toLowerCase()}`
    : isWrongClass
    ? `${targetClassLabel} is not included in your plan`
    : `${modeLabel} is locked`;

  const body = isSignIn
    ? "Free chapters are open to everyone. Sign in to access this chapter and every study mode -- textbook, notes, audio, video, flashcards, mind maps, quizzes, and your personal AI tutor."
    : isWrongClass
    ? `Your current subscription covers ${subscribedClassLabel}. You can browse ${targetClassLabel} free chapters, but premium study modes require a separate class access change.`
    : `Subscribe to ${targetClassLabel} to unlock every chapter and every study mode -- textbook, notes, audio, video, flashcards, mind maps, quizzes, and your personal AI tutor.`;

  return (
    <Container size="md" className="py-16">
      <FadeIn>
        <div className="rounded-2xl border border-border bg-surface p-6 sm:p-10 text-center">
          <div className="mx-auto grid place-items-center h-12 w-12 rounded-full bg-brand-soft text-brand">
            {isSignIn ? <LogIn className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
          </div>
          <h2 className="mt-5 font-display text-2xl font-semibold tracking-tight">
            {title}
          </h2>
          <p className="mt-2 text-text-muted text-sm sm:text-base max-w-md mx-auto">
            {body}
          </p>
          <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
            {isSignIn ? (
              <>
                <Button asChild>
                  <Link to={`/auth/login?next=${next}`}>Sign in</Link>
                </Button>
                <Button variant="secondary" asChild>
                  <Link to={`/auth/otp?next=${next}`}>Create account</Link>
                </Button>
              </>
            ) : isWrongClass ? (
              <>
                <Button asChild>
                  <Link to={subscribedClassId ? `/subjects?class=${encodeURIComponent(subscribedClassId)}` : "/subjects"}>
                    Go to {subscribedClassLabel}
                  </Link>
                </Button>
                <Button variant="secondary" asChild>
                  <Link to="/billing">Manage subscription</Link>
                </Button>
                {classId ? (
                  <Button variant="ghost" asChild>
                    <Link to={`/subjects?class=${encodeURIComponent(classId)}`}>
                      Browse {targetClassLabel}
                    </Link>
                  </Button>
                ) : null}
              </>
            ) : (
              <Button asChild>
                <Link to={subscribeHref}>Subscribe</Link>
              </Button>
            )}
            {!isWrongClass ? (
              <Button variant="ghost" asChild>
                <Link to="/subjects">Browse free chapters</Link>
              </Button>
            ) : null}
          </div>
        </div>
      </FadeIn>
    </Container>
  );
}

function classLabel(classId: string): string {
  const n = classId.replace(/^class-/, "");
  return /^\d+$/.test(n) ? `Class ${n}` : classId;
}

export function GenericError({
  title = "We couldn't load this",
  description = "Please retry in a moment, or try a different study mode.",
  onRetry,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
}) {
  const navigate = useNavigate();
  return (
    <Container size="md" className="py-16">
      <div className="rounded-2xl border border-border bg-surface p-8 text-center">
        <h2 className="font-display text-xl font-semibold tracking-tight">{title}</h2>
        <p className="mt-2 text-text-muted text-sm max-w-md mx-auto">{description}</p>
        <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
          {onRetry ? <Button onClick={onRetry}>Retry</Button> : null}
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
      </div>
    </Container>
  );
}
